import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { IconsModule } from '@progress/kendo-angular-icons';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { ListViewModule } from "@progress/kendo-angular-listview";
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { all } from '@awesome.me/kit-787edf9aa0/icons';
import { BehaviorSubject, Subscription } from 'rxjs';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { GridModule, KENDO_GRID } from '@progress/kendo-angular-grid';
import { PagerModule } from '@progress/kendo-angular-pager';
import { CompositeFilterDescriptor, SortDescriptor, distinct, filterBy, orderBy, } from '@progress/kendo-data-query';
import { DialogModule, WindowCloseResult, WindowModule, WindowRef, WindowService } from '@progress/kendo-angular-dialog';
import { ToastrService } from 'ngx-toastr';
import {
  CasiOwnerUser,
  CasiProjectLabel,
  CasiStoryEstimate, CasiStoryPriority, CasiStoryStatus,
  CasiStoryType, CasiUserStory, Project, User as UserModel
} from '../../models/models.lib';
import { LoggerService } from '../../shared/logger.service';
import { UserStoryService } from '../../tracker/user-story.service';
import { blastKeys, defaultGridPageSize, FormModes } from '../../common/constants.lib';
import { SafeHtmlPipe } from '../../pipes/safe-html.pipe';
import { StatusButtonComponent } from '../../common-components/status-button/status-button.component';
import { ProjectUserService } from '../../tracker/project-user.service';
import { ActivatedRoute } from '@angular/router';
import { LoadingIndicatorService } from '../../shared/loading-indicator.service';
import { SocketService } from '../../shared/socket.service';
import { formatText, setTitle, sortStatus } from '../../common/utils.lib';
import { UserStoryComponent } from '../user-story/user-story.component';
import { MulticheckFilterComponent } from '../../filters/multicheck-filter/multicheck-filter.component';

@Component({
  selector: 'casi-user-story-history',
  standalone: true,
  imports: [
    CommonModule,
    WindowModule,
    DialogModule,
    ListViewModule,
    LayoutModule,
    InputsModule,
    FormsModule,
    ReactiveFormsModule,
    IconsModule,
    MulticheckFilterComponent,
    ButtonsModule,
    InputsModule,
    DropDownsModule,
    FontAwesomeModule,
    GridModule,
    PagerModule,
    KENDO_GRID,
    SafeHtmlPipe,
    StatusButtonComponent
  ],
  templateUrl: './history.component.html',
  styleUrl: './history.component.scss'
})
export class HistoryComponent implements OnInit, OnDestroy {

  private _userStory: CasiUserStory | undefined;
  @Input({ required: true })
  set userStory(val: CasiUserStory | undefined) {
    this._userStory = val;
    this.logger.debug(`input:userStory: `, this._userStory);
    if (this._userStory) {
      this.doDataRequest();
    }
  }
  get userStory() {
    return (this._userStory as CasiUserStory);
  } //public userStory!: UserStory;

  public projects: Project[] = [];
  public users: UserModel[] = [];
  public projectUsers: CasiOwnerUser[] = [];
  public storyStatus: CasiStoryStatus[] = [];
  public storyTypes: CasiStoryType[] = [];
  public storyPriorities: CasiStoryPriority[] = [];
  public storyEstimates: CasiStoryEstimate[] = [];
  public projectLabels: CasiProjectLabel[] = [];
  public rawData: CasiUserStory[] = [];
  public sort: SortDescriptor[] = [
    {
      field: "id",
      dir: "desc",
    },
  ];

  public filter: CompositeFilterDescriptor = { logic: "and", filters: [] };
  public gridData: CasiUserStory[] = filterBy(this.rawData, this.filter);
  public selectedUserStory: CasiUserStory = this.gridData[0];
  public selectedUserStoryId = null;
  public currentUser: UserModel | undefined = this.users[0];
  public vTop: any = { 'vertical-align': 'top' };

  public editWinOpen: boolean = false;
  public addWinOpen: boolean = false;
  public formMode: FormModes = FormModes.NOTSET;
  public searchStr: string = '';
  public pageSize = defaultGridPageSize;
  public pageSizes = [20, 50, 100, 1000, 10000];

  private _showTitle = new BehaviorSubject<string>('');
  get showTitle() {
    return this._showTitle.getValue();
  }
  set showTitle(val: string) {
    this._showTitle.next(val);
  }

  private _subs: Subscription[] = [];

  private _windowRef: WindowRef | undefined;

  constructor(
    private logger: LoggerService,
    public faIcons: FaIconLibrary,
    private route: ActivatedRoute,
    private loadingIndictorService: LoadingIndicatorService,
    public userStoryService: UserStoryService,
    private toastr: ToastrService,
    private windowService: WindowService,
    private projectUserService: ProjectUserService,
    private socketService: SocketService
  ) {
    faIcons.addIcons(...all);
  }

  //#region NG event handlers

  ngOnInit(): void {
    // this.logger.debug('ngOnInit...');
    this.loadingIndictorService.loadingOff();

    this._subs.push(this.route.data.subscribe(({
      projects,
      users,
      storyStatus,
      storyTypes,
      storyPriorities,
      storyEstimates,
      projectLabels
    }) => {
      this.projects = projects.filter((p: Project) => {
        return p.active_user_stories == true;
      });
      this.users = users;
      this.storyStatus = storyStatus.sort(sortStatus);
      this.storyTypes = storyTypes;
      this.storyPriorities = storyPriorities;
      this.storyEstimates = storyEstimates;
      this.projectLabels = projectLabels;
      // this.logger.debug(`ngOnInit:users: `, this.users);
      // this.logger.debug(`ngOnInit:storyStatus: `, this.storyStatus);
      // this.logger.debug(`ngOnInit:storyEstimates: `, this.storyEstimates);
      // this.logger.debug(`ngOnInit:storyTypes: `, this.storyTypes);
      // this.logger.debug(`ngOnInit:projectLabels: `, this.projectLabels);
    }));

    this._subs.push(this.socketService.onRefresh()
      .subscribe((args: any | unknown) => {
        this.logger.debug(`onRefresh: `, args);
        if (args && args.blastKey === blastKeys.USER_STORY_UPDATED) {
          this.doDataRequest();
        }
      }));
  }

  ngOnDestroy(): void {
    // this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }

  //#endregion

  public formatText = formatText;

  onEditClick(dataItem: any, titleBar: TemplateRef<unknown>): void {
    this.logger.debug("onEditClick: ", dataItem);
    if (dataItem) {
      this.formMode = FormModes.EDIT;
      this.showTitle = setTitle(this.formMode, 'USER STORY');
      this.selectedUserStory = dataItem;
      this.selectedUserStoryId = dataItem.id ? dataItem.id : undefined;
      this.projectUserService.getByProjectId(dataItem.project_id)
        .then((data: any) => {
          this.projectUsers = data.map((r: any) => CasiOwnerUser.createFromProjectUser(r));
          this.logger.debug('onEditClick: projectUsers', this.projectUsers);
          this._windowRef = this.windowService.open({
            titleBarContent: titleBar,
            content: UserStoryComponent,
            width: 1000,
            minWidth: 320
          });
          this.setEditInstanceInputs(dataItem);
          this._windowRef.content.instance.submitForm.subscribe((formData: FormGroup) => {
            this.logger.debug('onEditClick:formData: ', formData);
            this.handleEditWinRefClose(formData.value, this.formMode);
          });

          this._windowRef.result.subscribe(results => {
            this.logger.debug('onEditClick:results: ', results);
            if (results instanceof WindowCloseResult) {
              this.closeEditWindowRef();
            }
          });

          this._windowRef.content.instance.closeForm.subscribe((formData: FormGroup) => {
            this.logger.debug('onEditClick:closeForm: formData: ', formData);
            this.closeEditWindowRef();
          });

          this.formMode = FormModes.EDIT;
          // this.showTitle = setTitle(this.formMode, 'PROJECT');
          this.editWinOpen = true;
        }).catch((error: any) => {
          this.logger.error(`onEditClick:error:`, error);
        });
    }
  }

  //#region Grid function

  public sortChange(sort: SortDescriptor[]): void {
    this.logger.debug("sortChange: ", sort);
    this.sort = sort;
    this.loadData();
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    // this.logger.debug('filterChange: filter: ', filter);
    this.filter = filter;
    this.loadData();
    // this.gridData = filterBy(this.rawData, filter);
  }

  public distinctPrimitive(fieldName: string): any[] {
    // this.logger.debug('distinctPrimitive: fieldName: ', fieldName);
    return distinct(this.rawData, fieldName).map((item: any) => item[fieldName]);
    // return distinct(this.gridData, fieldName).map((item: any) => item[fieldName]);
  }

  public copyStoryID(id: number): void {
    navigator.clipboard.writeText(id.toString());
    this.toastr.success(`Story ID ${id} copied to clipboard`);
  }

  public copyLink(us: CasiUserStory): void {
    this.logger.debug('us', us);
    let url = `${window.location.host}/userstory/show/${us.id}`;
    this.logger.debug('copy url', url);
    navigator.clipboard.writeText(url);
    this.toastr.success(`URL ${url} copied to clipboard`);
  }

  onEditCommentsClick(dataItem: any): void {
    this.logger.debug("onEditClick: ", dataItem);
  }

  //#endregion

  //#region Public functions

  public close(): void {
    this.logger.debug('close...');
    this.editWinOpen = false;
  }

  //#endregion

  //#region Private functions

  private handleWinRefClose(formData: any, mode: FormModes): void {
    this.logger.debug('handleWinRefClose...', formData);

    this.userStoryService.update(formData)
      .then(resp => {
        // this.showEmailInUse = false;
        // this.toastr.success('User Added');
        this.notify('User Story Added');
        // this.toastr.info(`Invitation Sent to ${toSubmit.email}`);
        this.closeWindowRef();
        // this.doDataRequest();
      })
      .catch(err => {
        this.logger.error('handleWinRefClose:', err);
        this.notify('Error saving user story', true);
      });
  }

  private setEditInstanceInputs(story: CasiUserStory): void {
    if (this._windowRef) {
      const inputs = this._windowRef.content.instance;
      inputs.selectedUserStoryId = this.selectedUserStoryId;
      inputs.showProjects = true;
      inputs.userStory = this.selectedUserStory;
      inputs.projects = this.projects;
      inputs.storyTypes = this.storyTypes;
      inputs.storyStatuses = this.storyStatus;
      inputs.storyPriorities = this.storyPriorities;
      inputs.storyEstimates = this.storyEstimates;
      inputs.users = this.users;
      inputs.userStory = story;
      inputs.projectLabels = this.projectLabels;
      inputs.modUsers = this.projectUsers;
      // inputs.formMode = this.formMode;
      this.logger.debug('setEditInstanceInputs: ', inputs);
    }
  }

  private closeWindowRef(tmp?: CasiUserStory): void {
    if (this._windowRef) {
      this._windowRef.close();
      this._windowRef = undefined;
    }
    this.editWinOpen = false;
    this.addWinOpen = false;
    if (tmp) {
      this.logger.debug('removeTmpUserStory: tmp: ', tmp);
      this.userStoryService.destroy(tmp.id!);
    }
  }

  private handleEditWinRefClose(formData: any, mode: FormModes): void {
    this.logger.debug('handleEditWinRefClose...', formData);
    this.closeEditWindowRef();
    // this.doDataRequest();
  }

  private closeEditWindowRef(tmp?: CasiUserStory): void {
    if (this._windowRef) {
      this._windowRef.close();
      this._windowRef = undefined;
    }
    this.editWinOpen = false;
    // this.doDataRequest();
  }

  private loadData(): void {
    this.gridData = orderBy(filterBy(this.rawData, this.filter), this.sort);
    // this.pageSize = this.gridData.length;
  }

  private doDataRequest(): void {
    if (this.userStory) {
      this.userStoryService
        .getUserStoryHistory(this.userStory.id!)
        .then(data => {
          if (data) {
            this.rawData = data;
            this.loadData();
          } else {
            this.rawData = [];
            this.loadData();
          }
        })
        .catch(err => {
          this.logger.error(`doDataRequest:error: `, err);
        });
    }
  }

  private notify(msg: string, error: boolean = false): void {
    this.logger.debug("notify", msg);
    if (error)
      this.toastr.error(msg);
    else
      this.toastr.success(msg);
  }

  //#endregion

}

